/**
 * Calendar attendant in the webmail (plan groupware-calendar.md §3.6): booking requests of rooms/equipment the user
 * approves as a delegate (approve / decline with a comment), and the user's own invitation settings (tentative
 * placeholders, cancelled meetings).
 */
import { useI18n } from '@platform/i18n';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Label,
  Skeleton,
  Textarea,
  toast,
  useAsync,
  useErrorText,
} from '@platform/ui';
import { CalendarCheckIcon, DoorOpenIcon, ProjectorIcon, RepeatIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { api, type BookingRequest, type MyAttendantSettings } from '../api';
import { useCalendarFormat } from './format';
import { useCalendar } from './calendar-context';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', declined: 'destructive', cancelled: 'muted' } as const;

export function BookingRequestsDialog({ open, onClose, onChanged }: { open: boolean; onClose: () => void; onChanged: () => void }) {
  const { t, formatNumber } = useI18n();
  const format = useCalendarFormat();
  const { zone } = useCalendar();
  const errorText = useErrorText();
  const [reloadKey, setReloadKey] = useState(0);
  const list = useAsync(() => (open ? api.bookingRequests() : Promise.resolve([])), `${open}:${reloadKey}`);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string>();
  const decide = async (request: BookingRequest, verdict: 'approve' | 'decline') => {
    setBusy(request.id);
    try {
      await api.decideBookingRequest(request.id, verdict, comments[request.id]?.trim() || undefined);
      toast.success(t(verdict === 'approve' ? 'webmail.calendar.booking.approvedToast' : 'webmail.calendar.booking.declinedToast', { subject: request.subject, resource: request.resourceName }));
      setReloadKey((k) => k + 1);
      onChanged();
    } catch (error) {
      errorText.notify(error);
      setReloadKey((k) => k + 1);
    } finally {
      setBusy(undefined);
    }
  };
  const requests = list.data ?? [];
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent framed bodySize="default" closeDisabled={!!busy} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendar.booking.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendar.booking.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody>{list.error ? <ErrorState description={errorText.describe(list.error)} onRetry={list.reload} /> : list.loading && !list.data ? <Skeleton className="h-32 w-full" /> : requests.length === 0 ? (
          <EmptyState icon={CalendarCheckIcon} title={t('webmail.calendar.booking.emptyTitle')} description={t('webmail.calendar.booking.emptyDescription')} />
        ) : (
          <ul className="flex flex-col divide-y" aria-label={t('webmail.calendar.booking.title')}>
            {requests.map((request) => (
              <li key={request.id} className="flex flex-col gap-2 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium break-words">{request.subject || t('webmail.calendar.noTitle')}</p>
                    <p className="text-sm text-muted-foreground">{format.range(zone.toWall(request.start), zone.toWall(request.end), false)} · {format.shortDay(zone.toWall(request.start))}</p>
                  </div>
                  <Badge variant={STATUS_VARIANT[request.status]}>{t(`webmail.calendar.booking.status.${request.status}`)}</Badge>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">{request.resourceKind === 'room' ? <DoorOpenIcon className="size-4" /> : <ProjectorIcon className="size-4" />}{request.resourceName}</span>
                  <span>{t('webmail.calendar.booking.organizer', { name: request.organizer.name })}</span>
                  <span className="flex items-center gap-1"><UsersIcon className="size-4" />{formatNumber(request.attendeeCount)}</span>
                  {request.recurring && <span className="flex items-center gap-1"><RepeatIcon className="size-4" />{t('webmail.calendar.booking.occurrences', { n: request.occurrences })}</span>}
                </div>
                {request.status === 'pending' ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <Textarea
                      aria-label={t('webmail.calendar.booking.comment')}
                      placeholder={t('webmail.calendar.booking.comment')}
                      value={comments[request.id] ?? ''}
                      onChange={(e) => setComments({ ...comments, [request.id]: e.target.value })}
                      rows={2}
                      className="min-h-16 flex-1"
                      disabled={busy === request.id}
                    />
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => void decide(request, 'decline')} disabled={busy !== undefined}>{t('webmail.calendar.booking.decline')}</Button>
                      <Button onClick={() => void decide(request, 'approve')} disabled={busy !== undefined}>{t('webmail.calendar.booking.approve')}</Button>
                    </div>
                  </div>
                ) : request.decidedBy && (
                  <p className="text-xs text-muted-foreground">{t('webmail.calendar.booking.decidedBy', { name: request.decidedBy })}{request.comment ? ` · ${request.comment}` : ''}</p>
                )}
              </li>
            ))}
          </ul>
        )}</DialogBody>
        <DialogFooter><Button variant="outline" disabled={!!busy} onClick={onClose}>{t('common.close')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvitationSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const current = useAsync(() => (open ? api.myAttendantSettings() : Promise.resolve(undefined)), String(open));
  const [draft, setDraft] = useState<MyAttendantSettings>();
  const value = draft ?? current.data;
  const [busy, setBusy] = useState(false);
  const flag = (key: 'enabled' | 'placeholders' | 'removeCancelled', label: 'webmail.calendar.invitations.enabled' | 'webmail.calendar.invitations.placeholders' | 'webmail.calendar.invitations.removeCancelled') =>
    value && (
      <Label className="flex items-start gap-2 text-sm font-normal">
        <Checkbox checked={value[key]} onCheckedChange={(checked) => setDraft({ ...value, [key]: checked === true })} disabled={busy || (key !== 'enabled' && !value.enabled)} />
        <span>{t(label)}</span>
      </Label>
    );
  const save = async () => {
    if (!value) return;
    setBusy(true);
    try {
      await api.setMyAttendantSettings({ enabled: value.enabled, placeholders: value.placeholders, removeCancelled: value.removeCancelled });
      toast.success(t('webmail.calendar.invitations.savedToast'));
      setDraft(undefined);
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { setDraft(undefined); onClose(); } }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendar.invitations.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendar.invitations.description')}</DialogDescription>
        </DialogHeader>
        {current.error ? <ErrorState description={errorText.describe(current.error)} onRetry={current.reload} /> : !value ? <Skeleton className="h-24 w-full" /> : (
          <div className="grid gap-3">
            {!value.attendantEnabled && <p className="text-sm text-muted-foreground">{t('webmail.calendar.invitations.off')}</p>}
            {flag('enabled', 'webmail.calendar.invitations.enabled')}
            {flag('placeholders', 'webmail.calendar.invitations.placeholders')}
            {flag('removeCancelled', 'webmail.calendar.invitations.removeCancelled')}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setDraft(undefined); onClose(); }} disabled={busy}>{t('common.cancel')}</Button>
          <Button onClick={() => void save()} disabled={busy || !value}>{t('common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
